import React, { useCallback, useEffect, useState } from "react";
import { DialogContent, Dialog, DialogTitle, IconButton, Box, Typography, Button } from "@mui/material";
import { CheckCircleOutlineOutlined, CloseOutlined } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { IPolicyVehicle } from "../../../models/user/policyDetails/PolicyDetails";
import { GetEditGarageLocationPAFormFields } from "../../../store/user/vehicle/vehicleActions";
import { IDynamicFormFieldsJson } from "../../../models/user/DynamicForm/DynamicForm";
import DynamicForm from "../../../common/DynamicForm/DynamicForm";
import { useTranslation } from "react-i18next";
import { setCalledReducerType } from "../../../store/common/CommonSlice";

interface UpdateGarageLocationPanelProps {
  vehicle: IPolicyVehicle;
  onAction?: (action: { followup_message?: string; label?: string }) => void;
  policyNumber?: string;
  onCancel?: () => void;
}

const UpdateGarageLocationPanel: React.FC<UpdateGarageLocationPanelProps> = ({
  vehicle,
  onAction,
  policyNumber,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { editGarageLocationPAFormFields } = useAppSelector((state: RootState) => state.vehicle);
  const { isSuccess, isError, calledReducerType } = useAppSelector((state: RootState) => state.common);
  const { policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);

  const [formFieldsJson, setFormFieldsJson] = useState<IDynamicFormFieldsJson | null>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    dispatch(GetEditGarageLocationPAFormFields());
  }, [dispatch]);

  useEffect(() => {
    if (editGarageLocationPAFormFields && vehicle) {
      const updatedFormFieldsJson = {
        ...editGarageLocationPAFormFields,
        sections:
          editGarageLocationPAFormFields.sections?.map((section) => ({
            ...section,
            fields: section?.fields.map((field) => {
              const updatedField: any = { ...field };
              if (updatedField.name === "address") {
                updatedField.value = vehicle?.address?.addr1;
              } else if (updatedField.name === "city") {
                updatedField.value = vehicle?.address?.city;
              } else if (updatedField.name === "state") {
                updatedField.value = vehicle?.address?.stateProvCd;
              } else if (updatedField.name === "zip") {
                updatedField.value = vehicle?.address?.postalCode;
              }
              return updatedField;
            }),
          })) || [],
      } as IDynamicFormFieldsJson;
      setFormFieldsJson(updatedFormFieldsJson);
    }
  }, [editGarageLocationPAFormFields, vehicle]);

  const handleFormSubmit = useCallback(() => {
    setMessageDialogOpen(true);
    const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
    if (pn && onAction) {
      onAction({ followup_message: `Show me details for policy ${pn}` });
    }
  }, [onAction, policyNumber, policyDetailsHeader]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  return (
    <>
      <DialogContent>
        {formFieldsJson ? (
          <>
            <DynamicForm
              formFieldsJson={formFieldsJson}
              onFormSubmit={handleFormSubmit}
              rightActions={
                <Button variant="contained" color="primary" onClick={handleCancel}>
                  {t("Cancel")}
                </Button>
              }
            />
          </>
        ) : (
          <></>
        )}
      </DialogContent>

      <Dialog open={messageDialogOpen} sx={{ backdropFilter: "blur(5px)" }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <IconButton onClick={handleMessageDialogClose} aria-label="Close message dialog">
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center", gap: 1, width: "100%" }}>
            <CheckCircleOutlineOutlined sx={{ fontSize: "60px", color: "green" }} />
            <Typography>{t("Policy Change has been initiated")}</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpdateGarageLocationPanel;
export { UpdateGarageLocationPanel };


